import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GridApi } from 'ag-grid-community';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { AgGridActionbutton } from 'src/app/shared/common/AgGridActionbutton';
import { JSIGNALBUTTON } from 'src/app/shared/common/JsignalButtons';
import { JsignalService } from 'src/app/shared/services/jsignal.service';
import * as moment from 'moment';

@Component({
    selector: 'app-ha-request-form',
    templateUrl: './ha-request-form.component.html',
    styleUrls: ['./ha-request-form.component.scss'],
    standalone: false
})
export class HaRequestFormComponent implements OnInit, OnChanges {
  // ==================== PUBLIC METHODS ====================
  onDeleteRow(e: any) {
    if (e && e.data) {
      if (e.data.actionName) {
        this.deleteAction(e.data);
      } else {
        this.deleteAttachment(e.data);
      }
    }
  }

  onEditRow(e: any) {
    if (e && e.data) {
      this.openEditSubActionDialog(e.data);
    }
  }

  // ==================== INPUTS & OUTPUTS ====================
  @Input() requestId: number | null = null;
  @Input() isViewOnly = false;

  @Output() back = new EventEmitter<void>();

  // ==================== FORM & UI STATE ====================
  form!: FormGroup;
  requestStatus: 'New' | 'In Progress' | 'Completed' | 'Cancelled' = 'New';
  selectedTab = 0;
  isLoading = false;
  
  // Pagination
  paginationPageSize = 10;

  // Edit Sub-Action Dialog
  showEditDialog = false;
  editingSubAction: any = null;
  editSubActionForm!: FormGroup;

  // ==================== GRID APIS ====================
  private gridApi!: GridApi;
  private attGridApi!: GridApi;

  // ==================== DATA STORES ====================
  subActions: any[] = [];
  attachments: any[] = [];
  links: any[] = [];
  combinedAttachments: any[] = [];

  // ==================== INPUT FIELDS ====================
  newActionText = '';
  newSubActionDueDate: Date | null = null;
  newSubActionStatus: string = 'Pending';
  newLink = '';

  // DNS ID (default to 1 as per controller)
  private dnsId = 1;

  // ==================== CONSTRUCTOR ====================
  constructor(
    private fb: FormBuilder,
    private jsignalService: JsignalService,
    private snackBar: MatSnackBar,
    private spinner: NgxUiLoaderService
  ) {}

  // ==================== LIFECYCLE HOOKS ====================
  ngOnInit(): void {
    this.buildForm();
    this.buildEditForm();
    
    if (!this.requestId) {
      this.initializeNewRequest();
    } else {
      this.loadData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requestId'] && !changes['requestId'].firstChange) {
      if (this.requestId) {
        this.loadData();
      } else {
        this.initializeNewRequest();
      }
    }
  }

  // ==================== DATA LOADING ====================
  private loadData(): void {
    if (!this.requestId) return;

    this.spinner.start();
    this.isLoading = true;
    
    this.jsignalService.getHaRequestById(this.requestId).subscribe({
      next: (res: any) => {
        this.mapDataToForm(res);
        this.isLoading = false;
        this.spinner.stop();
      },
      error: (error) => {
        console.error('Error loading request:', error);
        this.snackBar.open('Failed to load request', 'OK', { duration: 3000 });
        this.isLoading = false;
        this.spinner.stop();
      }
    });
  }

  private reloadData(): void {
    if (this.requestId) {
      this.loadData();
    }
  }

  private initializeNewRequest(): void {
    this.subActions = [];
    this.attachments = [];
    this.links = [];
    this.combinedAttachments = [];
    this.requestStatus = 'New';
    
    this.form.patchValue({
      id: '',
      source: '',
      subject: '',
      issueDate: null,
      dueDate: null,
      productName: '',
      inn: '',
      productType: '',
      productStatus: '',
      externalTrackingId: '',
      description: ''
    });
    
    this.refreshSubGrid();
    this.refreshAttGrid();
  }

  private mapDataToForm(res: any): void {
    const haRequest = res.haRequest || res;

    this.form.patchValue({
      id: haRequest.id,
      source: haRequest.source,
      subject: haRequest.subject,
      issueDate: haRequest.issueDate,
      dueDate: haRequest.dueDate,
      productName: haRequest.productName,
      inn: haRequest.inn,
      productType: haRequest.productType,
      productStatus: haRequest.productStatus,
      externalTrackingId: haRequest.externalTrackingId || '',
      description: haRequest.description
    });

    this.subActions = (res.subActions || []).map((a: any) => ({
      id: a.id,
      actionName: a.actionName,
      status: this.mapStatus(a.state),
      dueDate: a.dueDate ? new Date(a.dueDate) : null,
      _isDeleted: false
    }));

    this.attachments = (res.attachments || [])
      .filter((a: any) => a.type === 'FILE')
      .map((a: any) => ({
        id: a.id,
        name: a.name,
        type: 'FILE',
        filePath: a.filePath,
        size: a.size,
        fileType: a.fileType,
        _isDeleted: false
      }));

    this.links = (res.attachments || [])
      .filter((a: any) => a.type === 'LINK')
      .map((a: any) => ({
        id: a.id,
        name: a.name,
        url: a.url,
        type: 'LINK',
        _isDeleted: false
      }));

    this.requestStatus = this.mapStatus(haRequest.state || 'New');

    this.refreshSubGrid();
    this.refreshAttGrid();
  }

  // ==================== FORM BUILDING ====================
  private buildForm(): void {
    this.form = this.fb.group({
      id: [''],
      source: ['', Validators.required],
      subject: ['', Validators.required],
      issueDate: [null, Validators.required],
      dueDate: [null, Validators.required],
      productName: ['', Validators.required],
      inn: ['', Validators.required],
      productType: ['', Validators.required],
      productStatus: ['', Validators.required],
      externalTrackingId: [''],
      description: ['', Validators.required]
    });
  }

  private buildEditForm(): void {
    this.editSubActionForm = this.fb.group({
      actionName: ['', Validators.required],
      dueDate: [null, Validators.required],
      status: ['Pending', Validators.required]
    });
  }

  // ==================== STATUS MAPPING ====================
  private mapStatus(status: string): any {
    const map: any = {
      'New': 'New',
      'In Progress': 'In Progress',
      'Completed': 'Completed',
      'Cancelled': 'Cancelled',
      'PENDING': 'Pending',
      'IN_PROGRESS': 'In Progress',
      'COMPLETED': 'Completed',
      'CANCELLED': 'Cancelled',
      'NEW': 'New'
    };
    return map[status] || status;
  }

  private mapToBackendStatus(status: string): string {
    const map: any = {
      'New': 'New',
      'In Progress': 'In Progress',
      'Completed': 'Completed',
      'Cancelled': 'Cancelled',
      'Pending': 'Pending'
    };
    return map[status] || status;
  }

  // ==================== MODE GETTERS ====================
  get isEditMode(): boolean {
    return !!this.requestId;
  }

  // ==================== GRID CONFIGURATION ====================
  components = {
    jsignalButton: JSIGNALBUTTON,
    buttonRenderer: AgGridActionbutton
  };

  columnDefs = [
    { headerName: 'Action Name', field: 'actionName', flex: 2,...this.jsignalService.autoTooltip('Action Name')
 },
    { 
      headerName: 'Due Date', 
      field: 'dueDate', 
      flex: 1, 
      ...this.jsignalService.autoTooltip('Due Date')
    },
    { headerName: 'Status', field: 'status', flex: 1, ...this.jsignalService.autoTooltip('Status') },
    {
      headerName: 'Actions',
      width: 150,
      pinned: 'right',
      cellRenderer: 'buttonRenderer',
      cellRendererParams: (params: any) => {
        const row = params.data;
        return {
          showEdit: !this.isViewOnly,
          showDelete: !this.isViewOnly,
          edit: (e: any) => {
            this.onEditRow({ data: row });
          },
          delete: (e: any) => {
            this.onDeleteRow({ data: row });
          },
        };
      }
    }
  ];

  attColumnDefs = [
    { headerName: 'Name', field: 'name', flex: 2,...this.jsignalService.autoTooltip('Name'),
      cellRenderer: (params: any) => {
        const icon = params.data.type === 'FILE' ? '📄' : '🔗';
        return `<span style="cursor: pointer; color: #1976d2;">${icon} ${params.value}</span>`;
      },
      onCellClicked: (params: any) => {
        this.downloadOrOpenAttachment(params.data);
      }
    },
    { 
      headerName: 'Type', 
      field: 'type', 
      flex: 1,
      ...this.jsignalService.autoTooltip('Type'),
      valueFormatter: (params: any) => {
        return params.value === 'FILE' ? 'File' : 'Link';
      }
    },
    { 
      headerName: 'Size', 
      field: 'size', 
      flex: 1,
      ...this.jsignalService.autoTooltip('Size'),
      valueFormatter: (params: any) => {
        if (params.data.type === 'FILE' && params.value) {
          return this.formatFileSize(params.value);
        }
        return '-';
      }
    },
    {
      headerName: 'Actions',
      width: 150,
      pinned: 'right',
      cellRenderer: 'buttonRenderer',
      cellRendererParams: (params: any) => {
        const row = params.data;
        return {
          download: () => this.download(row),
          delete: () => this.deleteAttachment(row)
        };
      }
    }
  ];

  defaultColDef = { sortable: true, resizable: true };

  // ==================== GRID EVENT HANDLERS ====================
  onGridReady(p: any) {
    this.gridApi = p.api;
    this.refreshSubGrid();
  }

  onAttGridReady(p: any) {
    this.attGridApi = p.api;
    this.refreshAttGrid();
  }

  private refreshSubGrid() {
    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.subActions);
    }
  }

  private refreshAttGrid() {
    this.combinedAttachments = [...this.attachments, ...this.links];
    this.attGridApi?.setGridOption('rowData', this.combinedAttachments);
  }

  // ==================== SUB-ACTIONS MANAGEMENT ====================
  addSubAction() {
    if (!this.newActionText || !this.newSubActionDueDate) {
      this.snackBar.open('Please enter action name and due date', 'OK', { duration: 3000 });
      return;
    }

    if (this.isEditMode) {
      this.spinner.start();
      const payload = {
        actionName: this.newActionText,
        dueDate: this.formatDateForBackend(this.newSubActionDueDate),
        state: this.newSubActionStatus
      };
      this.jsignalService.createSubAction(this.requestId, payload).subscribe({
        next: () => {
          this.spinner.stop();
          this.snackBar.open('Sub-action added successfully', 'OK', { duration: 2000 });
          this.reloadData();
        },
        error: (error) => {
          console.error('Error adding sub-action:', error);
          this.snackBar.open('Failed to add sub-action', 'OK', { duration: 3000 });
          this.spinner.stop();
        }
      });
    } else {
      // Create mode - just add locally
      this.subActions.push({
        id: Date.now(),
        actionName: this.newActionText,
        dueDate: this.newSubActionDueDate,
        status: this.newSubActionStatus,
        _isDeleted: false
      });
      this.refreshSubGrid();
      this.snackBar.open('Sub-action added', 'OK', { duration: 2000 });
    }
    
    this.clearSubActionInputs();
  }

  private clearSubActionInputs() {
    this.newActionText = '';
    this.newSubActionDueDate = null;
    this.newSubActionStatus = 'Pending';
  }

  openEditSubActionDialog(row: any) {
    this.editingSubAction = row;
    this.editSubActionForm.patchValue({
      actionName: row.actionName,
      dueDate: row.dueDate,
      status: row.status
    });
    this.showEditDialog = true;
  }

  closeEditDialog() {
    this.showEditDialog = false;
    this.editingSubAction = null;
    this.editSubActionForm.reset();
  }

  saveEditedSubAction() {
    if (this.editSubActionForm.invalid) return;

    const updatedData = {
      actionName: this.editSubActionForm.value.actionName,
      dueDate: this.formatDateForBackend(this.editSubActionForm.value.dueDate),
      state: this.editSubActionForm.value.status
    };

    if (this.isEditMode && this.editingSubAction.id) {
      this.spinner.start();
      this.jsignalService.updateSubAction(this.editingSubAction.id, updatedData).subscribe({
        next: () => {
          this.spinner.stop();
          this.snackBar.open('Sub-action updated successfully', 'OK', { duration: 2000 });
          this.closeEditDialog();
          this.reloadData();
        },
        error: (error) => {
          console.error('Error updating sub-action:', error);
          this.snackBar.open('Failed to update sub-action', 'OK', { duration: 3000 });
          this.spinner.stop();
        }
      });
    } else {
      // Create mode - update locally
      this.editingSubAction.actionName = updatedData.actionName;
      this.editingSubAction.dueDate = this.editSubActionForm.value.dueDate;
      this.editingSubAction.status = this.editSubActionForm.value.status;
      this.refreshSubGrid();
      this.closeEditDialog();
      this.snackBar.open('Sub-action updated successfully', 'OK', { duration: 2000 });
    }
  }

  deleteAction(row: any) {
    if (!row) return;
    
    if (this.isEditMode && row.id) {
      this.spinner.start();
      this.jsignalService.deleteSubAction(row.id).subscribe({
        next: () => {
          this.spinner.stop();
          this.snackBar.open('Sub-action deleted successfully', 'OK', { duration: 2000 });
          this.reloadData();
        },
        error: (error) => {
          console.error('Error deleting sub-action:', error);
          this.snackBar.open('Failed to delete sub-action', 'OK', { duration: 3000 });
          this.spinner.stop();
        }
      });
    } else {
      // Create mode - remove locally
      const index = this.subActions.findIndex(a => a.id === row.id);
      if (index !== -1) {
        this.subActions.splice(index, 1);
      }
      this.refreshSubGrid();
      this.snackBar.open('Sub-action removed', 'OK', { duration: 2000 });
    }
  }

  // ==================== ATTACHMENTS MANAGEMENT ====================
  onFileSelected(e: any) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (this.isEditMode) {
      this.spinner.start();
      
      // Upload files one by one using the uploadMultipleFiles endpoint
      const uploadPromises: Promise<any>[] = [];
      
      Array.from(files).forEach((file: any) => {
        const formData = new FormData();
        formData.append('files', file);
        const promise = this.jsignalService.uploadMultipleFiles(this.requestId, formData, this.dnsId).toPromise();
        uploadPromises.push(promise);
      });
      
      Promise.all(uploadPromises).then(() => {
        this.spinner.stop();
        this.snackBar.open(`${files.length} file(s) uploaded successfully`, 'OK', { duration: 2000 });
        this.reloadData();
      }).catch((error) => {
        console.error('Error uploading files:', error);
        this.snackBar.open('Failed to upload files', 'OK', { duration: 3000 });
        this.spinner.stop();
      });
    } else {
      // Create mode - add locally
      Array.from(files).forEach((file: any) => {
        this.attachments.push({
          id: Date.now() + Math.random(),
          name: file.name,
          file: file,
          type: 'FILE',
          size: file.size,
          fileType: file.type,
          _isDeleted: false
        });
      });
      this.refreshAttGrid();
      this.snackBar.open(`${files.length} file(s) added`, 'OK', { duration: 2000 });
    }

    e.target.value = '';
  }

  addLink() {
    if (!this.newLink) {
      this.snackBar.open('Please enter a link', 'OK', { duration: 3000 });
      return;
    }

    let url = this.newLink;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    if (this.isEditMode) {
      this.spinner.start();
      const payload = {
        name: this.newLink,
        url: url,
        type: 'LINK'
      };
      this.jsignalService.addAttachmentLink(this.requestId, payload).subscribe({
        next: () => {
          this.spinner.stop();
          this.snackBar.open('Link added successfully', 'OK', { duration: 2000 });
          this.reloadData();
        },
        error: (error) => {
          console.error('Error adding link:', error);
          this.snackBar.open('Failed to add link', 'OK', { duration: 3000 });
          this.spinner.stop();
        }
      });
    } else {
      // Create mode - add locally
      this.links.push({
        id: Date.now() + Math.random(),
        name: this.newLink,
        url: url,
        type: 'LINK',
        _isDeleted: false
      });
      this.refreshAttGrid();
      this.snackBar.open('Link added', 'OK', { duration: 2000 });
    }

    this.newLink = '';
  }

  deleteAttachment(row: any) {
    if (!row) return;
    
    if (this.isEditMode && row.id) {
      this.spinner.start();
      this.jsignalService.deleteHAAttachment(row.id).subscribe({
        next: () => {
          this.spinner.stop();
          this.snackBar.open('Attachment deleted successfully', 'OK', { duration: 2000 });
          this.reloadData();
        },
        error: (error) => {
          console.error('Error deleting attachment:', error);
          this.snackBar.open('Failed to delete attachment', 'OK', { duration: 3000 });
          this.spinner.stop();
        }
      });
    } else {
      // Create mode - remove locally
      if (row.type === 'FILE') {
        const index = this.attachments.findIndex(a => a.id === row.id);
        if (index !== -1) {
          this.attachments.splice(index, 1);
        }
      } else {
        const index = this.links.findIndex(l => l.id === row.id);
        if (index !== -1) {
          this.links.splice(index, 1);
        }
      }
      this.refreshAttGrid();
      this.snackBar.open('Attachment removed', 'OK', { duration: 2000 });
    }
  }

  downloadOrOpenAttachment(row: any) {
    if (!row) return;
    
    if (row.type === 'FILE') {
      if (row.file) {
        // Local file (create mode)
        const url = window.URL.createObjectURL(row.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = row.name;
        a.click();
        window.URL.revokeObjectURL(url);
      } else if (row.id) {
        // Download from server using the download endpoint
        this.download(row);
      }
    } else if (row.type === 'LINK' && row.url) {
      window.open(row.url, '_blank');
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // ==================== FORM ACTIONS WITH FIXED MESSAGES ====================
  
  onSaveDraft() {
    if (this.form.invalid) {
      this.showValidationErrors();
      return;
    }
    
    if (this.isEditMode) {
      this.updateMainRequestOnly('New', 'Draft updated successfully');
    } else {
      this.createCompleteRequest('New', 'Draft saved successfully');
    }
  }

  onStartProgress() {
    if (this.form.invalid) {
      this.showValidationErrors();
      return;
    }
    
    if (this.isEditMode) {
      this.updateMainRequestOnly('In Progress', 'Request submitted successfully');
    } else {
      this.createCompleteRequest('In Progress', 'Request created and submitted successfully');
    }
  }

  onSaveChanges() {
    if (this.form.invalid) {
      this.showValidationErrors();
      return;
    }
    
    if (this.isEditMode) {
      this.updateMainRequestOnly(this.requestStatus, 'Changes saved successfully');
    } else {
      this.createCompleteRequest(this.requestStatus, 'Changes saved successfully');
    }
  }

  onCompleteRequest() {
    if (this.form.invalid) {
      this.showValidationErrors();
      return;
    }
    
    this.updateMainRequestOnly('Completed', 'Request completed successfully');
  }

  onCancelRequest() {
    if (this.form.invalid) {
      this.showValidationErrors();
      return;
    }
    
    this.updateMainRequestOnly('Cancelled', 'Request cancelled successfully');
  }

  private showValidationErrors(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
    this.snackBar.open('Please fill all required fields', 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
  }

  private saveToApi(status: string, successMessage: string) {
    this.spinner.start();
    
    if (this.isEditMode) {
      this.updateMainRequestOnly(status, successMessage);
    } else {
      this.createCompleteRequest(status, successMessage);
    }
  }

  private formatDateForBackend(date: any): string | null {
    if (!date) return null;
    
    let dateObj: Date;
    if (moment.isMoment(date)) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }
    
    // Format as ISO string without 'Z' suffix
    return dateObj.toISOString().replace('Z', '');
  }

  private updateMainRequestOnly(status: string, successMessage: string) {
    const formValue = this.form.value;

    const dto = {
      externalTrackingId: formValue.externalTrackingId || null,
      source: formValue.source,
      issueDate: this.formatDateForBackend(formValue.issueDate),
      dueDate: this.formatDateForBackend(formValue.dueDate),
      subject: formValue.subject,
      productName: formValue.productName,
      inn: formValue.inn,
      productType: formValue.productType,
      productStatus: formValue.productStatus,
      description: formValue.description,
      status: status,
      assignee: null,
      cancelReason: status === 'Cancelled' ? 'Cancelled by user' : null,
      subActions: [],
      attachments: []
    };

    this.jsignalService.updateHaRequest(this.requestId, dto).subscribe({
      next: (response: any) => {
        const message = response?.statusMessage || response?.message || successMessage;
        this.snackBar.open(message, 'OK', { duration: 3000 });
        this.spinner.stop();
        this.back.emit();
      },
      error: (error) => {
        console.error('Error updating request:', error);
        const errorMessage = error.error?.statusMessage || error.error?.message || 'Failed to update request';
        this.snackBar.open(errorMessage, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        this.spinner.stop();
      }
    });
  }

  private createCompleteRequest(status: string, successMessage: string) {
    const formValue = this.form.value;

    const dto = {
      externalTrackingId: formValue.externalTrackingId || null,
      source: formValue.source,
      issueDate: this.formatDateForBackend(formValue.issueDate),
      dueDate: this.formatDateForBackend(formValue.dueDate),
      subject: formValue.subject,
      productName: formValue.productName,
      inn: formValue.inn,
      productType: formValue.productType,
      productStatus: formValue.productStatus,
      description: formValue.description,
      status: status,
      assignee: null,
      cancelReason: status === 'Cancelled' ? 'Cancelled by user' : null,
      subActions: this.subActions.map(a => ({
        actionName: a.actionName,
        dueDate: this.formatDateForBackend(a.dueDate),
        state: a.status
      })),
      attachments: [
        ...this.links.map(l => ({
          name: l.name,
          url: l.url,
          type: l.type
        })),
        ...this.attachments
          .filter(a => !a._isDeleted)
          .map(a => ({
            name: a.name,
            type: a.type,
            file: a.file || null,
            filePath: a.filePath || null
          }))
      ]
    };

    const files = this.attachments.filter(a => a.file).map(a => a.file);

    const formData = new FormData();
    formData.append('request', new Blob([JSON.stringify(dto)], { type: 'application/json' }));

    files.forEach((file: File) => {
      formData.append('files', file);
    });

    this.jsignalService.createHaRequest(formData).subscribe({
      next: (response: any) => {
        const message = response?.statusMessage || response?.message || successMessage;
        this.snackBar.open(message, 'OK', { duration: 3000 });
        this.spinner.stop();
        this.back.emit();
      },
      error: (error) => {
        console.error('Error creating request:', error);
        const errorMessage = error.error?.statusMessage || error.error?.message || 'Failed to create request';
        this.snackBar.open(errorMessage, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        this.spinner.stop();
      }
    });
  }

  // ==================== UI HELPERS ====================
  get headerTitle(): string {
    return this.requestId ? `HA Request #${this.requestId}` : 'New HA Request';
  }

  get showDraftActions(): boolean {
    return this.requestStatus === 'New';
  }

  get showInProgressActions(): boolean {
    return this.requestStatus === 'In Progress';
  }

  get pendingCount(): number {
    return this.subActions.filter(a => a.status === 'Pending').length;
  }

  get inProgressCount(): number {
    return this.subActions.filter(a => a.status === 'In Progress').length;
  }

  get completedCount(): number {
    return this.subActions.filter(a => a.status === 'Completed').length;
  }

  download(params: any) {
    this.snackBar.open("Downloading attachment... please wait.", "Ok", { duration: 5000 }); 
    this.spinner.start();

    this.jsignalService.downloadFileAttachment(params.id).subscribe({
      next: (res) => {
        const blob = res.body as Blob;
        const contentDisposition = res.headers.get('Content-Disposition');
        let fileName = params.name;

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="(.+)"/);
          if (match && match[1]) {
            fileName = match[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        this.spinner.stop();
      },
      error: () => {
        this.snackBar.open("Download failed", "Ok", { duration: 3000 });
        this.spinner.stop();
      }
    });
  }
}